import { useClerk, useUser } from "@clerk/clerk-react";
import type { AppSurface } from "@/components/UserMenu/UserMenu";
import type { OrganizerClub } from "@/lib/clubs/queries";
import styles from "./WelcomeHub.module.scss";

interface WelcomeHubProps {
  accessibleSurfaces: readonly AppSurface[];
  clubs: OrganizerClub[];
}

interface SurfaceRow {
  surface: AppSurface;
  label: string;
  href: string;
  description: string;
}

const SURFACES: SurfaceRow[] = [
  {
    surface: "cockpit",
    label: "Organizer cockpit",
    href: "/clubs",
    description: "Run your clubs, schedule gatherings, manage voting.",
  },
  {
    surface: "operator",
    label: "Operator console",
    href: "/staff",
    description: "Platform-wide rooms, prompts, discoverability.",
  },
  {
    surface: "facilitator",
    label: "Facilitator dashboard",
    href: "/facilitate",
    description: "Spot stragglers in the rooms you facilitate.",
  },
];

export function WelcomeHub({ accessibleSurfaces, clubs }: WelcomeHubProps) {
  const { user } = useUser();
  const { signOut } = useClerk();

  const surfaces = SURFACES.filter((s) =>
    accessibleSurfaces.includes(s.surface),
  );
  const displayName =
    user?.fullName ?? user?.username ?? user?.firstName ?? "Welcome";
  const email = user?.primaryEmailAddress?.emailAddress ?? "";

  const handleSignOut = () => {
    void signOut({ redirectUrl: "/sign-in" });
  };

  return (
    <main className={styles.root}>
      <header className={styles.head}>
        <p className={styles.eyebrow}>Welcome back</p>
        <h1 className={styles.h1}>{displayName}</h1>
        {email && <p className={styles.lede}>{email}</p>}
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionLabel}>Surfaces</h2>
        <ol className={styles.list}>
          {surfaces.map((s) => (
            <li key={s.surface}>
              <a href={s.href} className={styles.row}>
                <span className={styles.rowMain}>
                  <span className={styles.rowTitle}>{s.label}</span>
                  <span className={styles.rowDesc}>{s.description}</span>
                </span>
                <span className={styles.rowArrow} aria-hidden>
                  →
                </span>
              </a>
            </li>
          ))}
        </ol>
      </section>

      {clubs.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>Clubs you organize</h2>
          <ol className={styles.list}>
            {clubs.map((club) => (
              <li key={club.id}>
                <a href={`/clubs/${club.id}`} className={styles.row}>
                  <span className={styles.rowMain}>
                    <span className={styles.rowTitle}>{club.name}</span>
                  </span>
                  <span className={styles.rowArrow} aria-hidden>
                    →
                  </span>
                </a>
              </li>
            ))}
          </ol>
        </section>
      )}

      <footer className={styles.foot}>
        <button
          type="button"
          className={styles.signOut}
          onClick={handleSignOut}
        >
          Sign out
        </button>
      </footer>
    </main>
  );
}
